import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronsUpDown, ChevronUp, ChevronDown, Info } from "lucide-react";
import Timestamp from "../components/Timestamp";
import Tooltip from "../components/Tooltip";
import PageHeader from "../components/PageHeader";
import Button from "../components/Button";
import ToggleGroup from "../components/ToggleGroup";
import { useAuth } from "../contexts/AuthContext";
import { usePageTitle } from "../hooks/usePageTitle";
import { useVisibleApps } from "../hooks/useVisibleApps";

function ScopesCount({ scopes }: { scopes: string }) {
  const scopeList = scopes
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const count = scopeList.length;

  return (
    <Tooltip
      content={
        <div className="text-left">
          <div className="font-semibold mb-1">Scopes</div>
          <ul className="space-y-0.5">
            {scopeList.map((scope, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-gray-400 dark:text-dark-text-muted">•</span>
                <span className="font-mono">{scope}</span>
              </li>
            ))}
          </ul>
        </div>
      }
      position="top"
    >
      <span className="text-sm text-gray-500 dark:text-dark-text-secondary cursor-default">
        {count} {count === 1 ? "scope" : "scopes"}
      </span>
    </Tooltip>
  );
}

function InstallsCount({ count, shops }: { count: number; shops: string[] }) {
  if (count === 0) {
    return <span className="text-sm text-gray-400 dark:text-dark-text-muted">No installs</span>;
  }

  return (
    <Tooltip
      content={
        <div className="text-left">
          <div className="font-semibold mb-1">Installed Shops</div>
          <ul className="space-y-0.5">
            {shops.map((shop, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-gray-400 dark:text-dark-text-muted">•</span>
                <span className="font-mono">{shop}</span>
              </li>
            ))}
          </ul>
        </div>
      }
      position="top"
    >
      <span className="text-sm text-gray-500 dark:text-dark-text-secondary cursor-default">
        {count} {count === 1 ? "install" : "installs"}
      </span>
    </Tooltip>
  );
}

type SortField = "name" | "clientId" | "createdAt";
type SortDirection = "asc" | "desc";

function SortIcon({ active, direction }: { active: boolean; direction: SortDirection }) {
  if (!active) {
    return <ChevronsUpDown className="w-4 h-4 text-gray-400 dark:text-dark-text-muted" />;
  }
  return direction === "asc" ? (
    <ChevronUp className="w-4 h-4 text-primary-500 dark:text-primary-400" />
  ) : (
    <ChevronDown className="w-4 h-4 text-primary-500 dark:text-primary-400" />
  );
}

export default function AppsPage() {
  usePageTitle("Apps");
  const navigate = useNavigate();
  const { isAdmin, isDeveloper } = useAuth();
  const { apps, allApps, loading, error, viewFilter, setViewFilter } = useVisibleApps();

  const canCreateApps = isAdmin || isDeveloper;
  const canFilterApps = isAdmin || isDeveloper;

  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const sortedApps = useMemo(() => {
    return [...apps].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "clientId":
          comparison = a.clientId.localeCompare(b.clientId);
          break;
        case "createdAt":
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [apps, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" aria-busy="true" aria-live="polite">
        <div className="text-gray-500 dark:text-dark-text-secondary">Loading apps...</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader>
        <PageHeader.TitleRow>
          <PageHeader.TitleGroup>
            <PageHeader.Title>Dashboard</PageHeader.Title>
            <PageHeader.Subtitle>
              Generate non-expiring offline tokens for service-to-service Shopify integrations
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          <PageHeader.Actions>
            {canFilterApps && (
              <ToggleGroup value={viewFilter} onChange={setViewFilter}>
                <ToggleGroup.Option value="all">All</ToggleGroup.Option>
                <ToggleGroup.Option value="mine">Mine</ToggleGroup.Option>
              </ToggleGroup>
            )}
            {canCreateApps && (
              <Button size="sm" as={Link} to="/apps/new">
                Register New App
              </Button>
            )}
          </PageHeader.Actions>
        </PageHeader.TitleRow>
      </PageHeader>

      {error && (
        <div
          role="alert"
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-6"
        >
          {error}
        </div>
      )}

      {apps.length === 0 ? (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-12 text-center">
          <div className="text-gray-400 dark:text-dark-text-muted text-5xl mb-4">📦</div>
          <h2 className="text-xl font-semibold text-gray-700 dark:text-dark-text mb-2">
            {viewFilter === "mine" && allApps.length > 0
              ? "No apps created by you"
              : "No apps registered"}
          </h2>
          <p className="text-gray-500 dark:text-dark-text-secondary mb-6">
            {viewFilter === "mine" && allApps.length > 0
              ? 'You haven\'t created any apps yet. Switch to "All" to see all apps.'
              : canCreateApps
                ? "Register your first Shopify app to get started."
                : "No apps have been registered yet."}
          </p>
          {canCreateApps && allApps.length === 0 && (
            <Link
              to="/apps/new"
              className="inline-flex px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500 transition-colors"
            >
              Register New App
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider"
                >
                  <button
                    onClick={() => handleSort("name")}
                    className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-dark-text transition-colors"
                    aria-label={`Sort by name ${sortField === "name" ? (sortDirection === "asc" ? "descending" : "ascending") : "ascending"}`}
                  >
                    Name
                    <SortIcon active={sortField === "name"} direction={sortDirection} />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider"
                >
                  <button
                    onClick={() => handleSort("clientId")}
                    className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-dark-text transition-colors"
                    aria-label={`Sort by client ID ${sortField === "clientId" ? (sortDirection === "asc" ? "descending" : "ascending") : "ascending"}`}
                  >
                    Client ID
                    <SortIcon active={sortField === "clientId"} direction={sortDirection} />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider"
                >
                  Installs
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider"
                >
                  Scopes
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider"
                >
                  <button
                    onClick={() => handleSort("createdAt")}
                    className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-dark-text transition-colors"
                    aria-label={`Sort by created date ${sortField === "createdAt" ? (sortDirection === "asc" ? "descending" : "ascending") : "ascending"}`}
                  >
                    Created
                    <SortIcon active={sortField === "createdAt"} direction={sortDirection} />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
              {sortedApps.map((app) => (
                <tr
                  key={app.id}
                  onClick={() => navigate(`/apps/${app.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/apps/${app.id}`);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`View ${app.name}`}
                  className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary cursor-pointer focus:bg-gray-50 dark:focus:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                >
                  <td className="px-6 py-4 whitespace-nowrap align-middle">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-dark-text">
                        {app.name}
                      </span>
                      {app.description && (
                        <Tooltip content={app.description} position="top">
                          <span
                            className="text-gray-300 dark:text-dark-text-muted hover:text-gray-500 dark:hover:text-dark-text-secondary transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Info className="w-4 h-4" />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle">
                    <code className="text-sm text-gray-600 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg-tertiary px-2 py-1 rounded">
                      {app.clientId}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle">
                    <InstallsCount count={app.installCount ?? 0} shops={app.installedShops ?? []} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle">
                    <ScopesCount scopes={app.scopes} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-sm text-gray-500 dark:text-dark-text-secondary">
                    <Timestamp date={app.createdAt} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-right">
                    <span className="text-primary-500 dark:text-primary-400 font-medium text-sm">
                      View →
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
